import React from "react";
import { Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { getFooter } from "../../../utils/helperUtils";

const styles = StyleSheet.create({
    page: {
        padding: "40px 50px",
        backgroundColor: "#ffffff",
        position: "relative",
        minHeight: "100%",
    },
    content: {
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        paddingBottom: "60px",
    },
    headerSection: {
        textAlign: "center",
        marginBottom: "20px",
    },
    infoSection: {
        textAlign: "center",
        marginBottom: "15px",
        padding: "12px",
        borderRadius: "5px",
    },
    title: {
        fontSize: 22,
        fontWeight: "bold",
        marginBottom: "12px",
        color: "#2c3e50",
    },
    subtitle: {
        fontSize: 16,
        fontWeight: "medium",
        color: "#34495e",
        marginBottom: "6px",
    },
    sectionTitle: {
        fontSize: 13,
        fontWeight: "bold",
        color: "#2c3e50",
        marginBottom: "8px",
    },
    text: {
        fontSize: 11,
        lineHeight: 1.3,
        color: "#34495e",
    },
    mainContainer: {
        flex: 1,
        position: "relative",
    },
});

interface IIntroductionPageProps {
    data: any;
    confidentialInfo: string;
}

export const IntroductionPage: React.FC<IIntroductionPageProps> = ({ data, confidentialInfo }) => {
    const { exportAppealsPackage } = data.appealApproachCalculationData;
    const { coverPage, salientFacts } = exportAppealsPackage;
    const getDayAndFormattedDate = (dateString: string): string => {
        if (!dateString) {
            return "";
        }

        const date = new Date(dateString);

        // Check if the date is valid
        if (isNaN(date.getTime())) {
            return "";
        }

        const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
        const months = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
        ];

        const dayOfWeek = days[date.getDay()];
        const month = months[date.getMonth()];
        const day = date.getDate();
        const year = date.getFullYear();

        return `${dayOfWeek}, ${month} ${day}, ${year}`;
    };

    const formatTime = (time: string): string => {
        if (!time || time?.trim() === "") return "";

        const [hours, minutes] = time.split(":").map(Number);

        if (isNaN(hours) || isNaN(minutes)) return "";

        const period = hours >= 12 ? "PM" : "AM";
        const formattedHours = hours % 12 || 12;

        return `${formattedHours}:${minutes.toString().padStart(2, "0")} ${period}`;
    };

    return (
        <Page size="A4" style={styles.page}>
            <View style={styles.mainContainer}>
                <View style={styles.content}>
                    <View style={styles.headerSection}>
                        <Text style={styles.title}>Assessment Documentation</Text>
                        <Text style={styles.subtitle}>{salientFacts.county} County</Text>
                        <Text style={styles.subtitle}>{salientFacts.jurisdiction}</Text>
                    </View>

                    <View style={styles.infoSection}>
                        <Text style={styles.sectionTitle}>Subject Property</Text>
                        <Text style={styles.text}>{coverPage.subjectPropertyOwner}</Text>
                        <Text style={styles.text}>{coverPage.subjectPropertyNumber} - Amazon</Text>
                        <Text style={styles.text}>
                            {coverPage.subjectPropertyAddress.streetaddress}
                        </Text>
                        <Text style={styles.text}>
                            {`${coverPage.subjectPropertyAddress.city}, ${coverPage.subjectPropertyAddress.state} ${coverPage.subjectPropertyAddress.zipCode}`}
                        </Text>
                    </View>

                    <View style={styles.infoSection}>
                        <Text style={styles.sectionTitle}>Hearing Information</Text>
                        <Text style={styles.text}>
                            {getDayAndFormattedDate(coverPage.hearingDate)}
                        </Text>
                        <Text style={styles.text}>{formatTime(coverPage.hearingTime)}</Text>
                    </View>

                    <View style={styles.infoSection}>
                        <Text style={styles.sectionTitle}>Amazon</Text>
                        <Text style={styles.text}>{coverPage.amazonAddress.poBoxNumber}</Text>
                        <Text style={styles.text}>
                            {`${coverPage.amazonAddress.city}, 
                              ${coverPage.amazonAddress.state} 
                              ${coverPage.amazonAddress.zipCode}`}
                        </Text>
                    </View>

                    <View style={styles.infoSection}>
                        <Text style={styles.sectionTitle}>Presented by</Text>
                        <Text style={styles.text}>{coverPage.presenter}</Text>
                        <Text style={styles.text}>Property Tax - Amazon.com</Text>
                        <Text style={styles.text}>{coverPage.contactInformation}</Text>
                    </View>
                </View>

                {getFooter(confidentialInfo)}
            </View>
        </Page>
    );
};
